import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { URL } from "../../core/config";

const usersURL = `${URL}/users`;

export function userIdKey(id) {
  if (!id) return "";
  if (typeof id === "object") return String(id._id || id);
  return String(id);
}

/** Every user ref attached to an event (organizers, staff, alumni). */
export function collectEventUserRefs(events) {
  return (events || []).flatMap((event) => [
    event?.mo,
    event?.vmo,
    ...(event?.mos || []),
    ...(event?.vmos || []),
    ...(event?.participants || []),
    ...(event?.alumni || []),
  ]);
}

/** Ids on events that are not on the active semester roster (Unbound, Alumni, etc.). */
export function offRosterUserIds(userRefs, rosterUsers, rosterLoaded) {
  if (!rosterLoaded) return [];
  const rosterIds = new Set((rosterUsers || []).map((user) => userIdKey(user._id)));
  return [...new Set(userRefs.map(userIdKey).filter(Boolean))].filter(
    (id) => !rosterIds.has(id),
  );
}

/**
 * Loads user profiles for specific ids via GET /users/:id (parallel).
 * Intended for people outside the active semester roster (Alumni, Unbound, etc.).
 */
function useFetchUsersByIds(userIds) {
  const [usersById, setUsersById] = useState({});
  const loadedRef = useRef(new Set());
  const inFlightRef = useRef(new Set());

  const idsKey = [...new Set((userIds || []).map(userIdKey).filter(Boolean))]
    .sort()
    .join(",");

  useEffect(() => {
    if (!idsKey) return undefined;

    const missing = idsKey
      .split(",")
      .filter((id) => !loadedRef.current.has(id) && !inFlightRef.current.has(id));

    if (missing.length === 0) return undefined;

    missing.forEach((id) => inFlightRef.current.add(id));
    let cancelled = false;

    axios
      .get(`${usersURL}/by-ids`, { params: { ids: missing.join(",") } })
      .then((response) => response.data || [])
      .catch(() => [])
      .then((rows) => {
        if (cancelled) {
          missing.forEach((id) => inFlightRef.current.delete(id));
          return;
        }

        const found = rows.filter((user) => user && user._id);
        if (found.length > 0) {
          setUsersById((prev) => {
            const next = { ...prev };
            found.forEach((user) => {
              const key = userIdKey(user._id);
              loadedRef.current.add(key);
              next[key] = user;
            });
            return next;
          });
        }

        missing.forEach((id) => {
          inFlightRef.current.delete(id);
          if (!loadedRef.current.has(id)) {
            loadedRef.current.add(id);
          }
        });
      });

    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  const findUserById = useCallback(
    (id) => {
      const key = userIdKey(id);
      return key ? usersById[key] : undefined;
    },
    [usersById],
  );

  return { findUserById, usersById };
}

export default useFetchUsersByIds;
